import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaEnvelope, FaLock, FaMagic, FaUserSecret, FaShareAlt, FaCrown, FaMobileAlt, FaUsers, FaPaperPlane } from 'react-icons/fa';
import Footer from '../components/Footer';

const AnimatedStat = ({ endValue, label, icon: Icon, suffix = "" }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    // Generate a random starting point between 80-90% of endValue
    const startValue = Math.floor(endValue * (0.8 + Math.random() * 0.1));
    setCount(startValue);

    // Update count randomly
    const interval = setInterval(() => {
      setCount(current => {
        // Random increment between -2 and +5
        const change = Math.floor(Math.random() * 8) - 2;
        const newValue = current + change;
        
        // Special handling for satisfaction rate
        if (label === "Satisfaction Rate") {
          if (newValue > 99) return 98;
          return newValue;
        }
        
        // Keep within reasonable bounds for other stats
        if (newValue > endValue * 1.1) return current - 3;
        if (newValue < endValue * 0.8) return current + 3;
        return newValue;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [endValue, label]); // Added label to dependencies

  return (
    <div className="text-center group">
      <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-white/10 backdrop-blur-sm text-white/90 group-hover:bg-white/20 transition-all duration-300">
        <Icon className="w-8 h-8" />
      </div>
      <div className="text-3xl font-bold text-white transition-all duration-300">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="mt-1 text-white/80">{label}</div>
    </div>
  );
};

const StatsSection = () => {
  // Dynamic base values that will fluctuate
  const baseStats = {
    users: 14800,
    messages: 98500,
    satisfaction: 98,
    daily: 28500
  };

  return (
    <div className="relative py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Join Our Growing Community
          </h2>
          <p className="mt-3 text-xl text-white/80">
            Be part of thousands sharing meaningful messages every day
          </p>
        </div>

        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <AnimatedStat
            endValue={baseStats.users}
            label="Active Users"
            icon={FaUsers}
            suffix="+"
          />
          <AnimatedStat
            endValue={baseStats.messages}
            label="Messages Sent"
            icon={FaEnvelope}
            suffix="+"
          />
          <AnimatedStat
            endValue={baseStats.satisfaction}
            label="Satisfaction Rate"
            icon={FaHeart}
            suffix="%"
          />
          <AnimatedStat
            endValue={baseStats.daily}
            label="Daily Messages"
            icon={FaPaperPlane}
            suffix="+"
          />
        </div>

        {/* Subtle animated background elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/5 rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-32 h-32 bg-white/5 rounded-full mix-blend-overlay filter blur-3xl animate-pulse delay-1000"></div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <div className="min-h-screen relative bg-gradient-to-br from-purple-600 to-pink-600 flex flex-col text-white">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-white/10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-black/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
        
        {/* Falling hearts */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-fall"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-50px`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${Math.random() * 5 + 8}s`,
              opacity: 0.3,
            }}
          >
            <FaHeart
              className="text-white animate-heart-spin"
              style={{
                fontSize: `${Math.random() * 15 + 10}px`,
              }}
            />
          </div>
        ))}

        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-xl animate-float-slow"></div>
        <div className="absolute bottom-1/3 left-1/3 w-48 h-48 bg-white/5 rounded-full blur-xl animate-float-delayed"></div>
      </div>

      <div className="relative flex-1 z-10">
        {/* Hero Section */}
        <div className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                <span className="text-white inline-block">
                  Share Your Thoughts
                </span>
                <br />
                <span className="text-white/90 inline-block mt-2">
                  Anonymously & Beautifully
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
                Create your personal inbox for anonymous messages. Share your link and receive heartfelt messages from friends, family, or secret admirers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <FaMagic className="mr-2" />
                  Create Your Link
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium border border-white/20 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <FaEnvelope className="mr-2" />
                  Check Messages
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaUserSecret className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">100% Anonymous</h3>
                <p className="text-white/80 text-sm sm:text-base">Send and receive messages without revealing your identity. Your privacy is our priority.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaLock className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Secure & Private</h3>
                <p className="text-white/80 text-sm sm:text-base">Your messages are encrypted and protected. Only you can access your inbox.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaHeart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Beautiful Design</h3>
                <p className="text-white/80 text-sm sm:text-base">Experience messaging in a beautifully designed interface with love letter aesthetics.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaShareAlt className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Easy Sharing</h3>
                <p className="text-white/80 text-sm sm:text-base">Share your messages on social media or download them as beautiful images.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaCrown className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Premium Features</h3>
                <p className="text-white/80 text-sm sm:text-base">Unlock advanced features like custom themes and more with our premium plan.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] transform hover:scale-105 transition-all duration-200 hover:bg-white/[0.15]">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <FaMobileAlt className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Mobile Friendly</h3>
                <p className="text-white/80 text-sm sm:text-base">Access your messages anywhere with our responsive mobile-first design.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section - now after features */}
        <div className="relative z-10">
          <StatsSection />
        </div>
      </div>
      
      <div className="mt-8 border-t border-white/10">
        <Footer variant="dark" />
      </div>
    </div>
  );
};

export default Home; 